import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Progress } from '../components/ui/progress';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import {
  AlertTriangle,
  Clock,
  Users,
  FileText,
  CheckCircle2,
  TrendingUp,
  ArrowRight,
  RefreshCw,
  Loader2,
  Bell,
  Target,
  Phone,
  Sparkles,
  Heart,
  Package,
  Truck,
  Eye
} from 'lucide-react';
import { toast } from 'sonner';
import api from '../lib/api';

// Icônes et couleurs par étape
const ETAPE_CONFIG = {
  premier_contact: { icon: Phone, color: 'text-blue-600 bg-blue-100', label: 'Premier Contact' },
  decouverte: { icon: Sparkles, color: 'text-purple-600 bg-purple-100', label: 'Découverte' },
  fidelisation: { icon: Heart, color: 'text-pink-600 bg-pink-100', label: 'Fidélisation' },
  proposition: { icon: FileText, color: 'text-orange-600 bg-orange-100', label: 'Proposition' },
  invitation_visite: { icon: Users, color: 'text-green-600 bg-green-100', label: 'Invitation & Visite' },
  montage: { icon: Package, color: 'text-indigo-600 bg-indigo-100', label: 'Montage' },
  commande_livraison: { icon: Truck, color: 'text-emerald-600 bg-emerald-100', label: 'Commande & Livraison' }
};

const PRIORITE_CONFIG = {
  haute: { color: 'bg-red-100 text-red-700 border-red-200', label: 'Haute', icon: '🔴' },
  moyenne: { color: 'bg-orange-100 text-orange-700 border-orange-200', label: 'Moyenne', icon: '🟠' },
  basse: { color: 'bg-yellow-100 text-yellow-700 border-yellow-200', label: 'Basse', icon: '🟡' }
};

const ParcoursDashboardPage = () => {
  const [loading, setLoading] = useState(true);
  const [alerts, setAlerts] = useState([]);
  const [alertStats, setAlertStats] = useState({});
  const [dashboard, setDashboard] = useState(null);
  const [filterPriorite, setFilterPriorite] = useState('all');

  const fetchData = async () => {
    setLoading(true);
    try {
      const [alertsRes, dashboardRes] = await Promise.all([
        api.get('/parcours/alerts?limit=100'),
        api.get('/parcours/dashboard')
      ]);
      
      setAlerts(alertsRes.data.alerts || []);
      setAlertStats(alertsRes.data.stats || {});
      setDashboard(dashboardRes.data);
    } catch (error) {
      console.error('Erreur chargement données:', error);
      toast.error('Erreur lors du chargement');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchData();
  }, []);

  const filteredAlerts = filterPriorite === 'all' 
    ? alerts 
    : alerts.filter(a => a.priorite === filterPriorite);

  const formatRetard = (heures) => {
    if (heures < 24) return `${Math.round(heures)}h de retard`;
    const jours = Math.round(heures / 24);
    return `${jours}j de retard`;
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-96">
        <Loader2 className="w-8 h-8 animate-spin text-orange-500" />
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="parcours-dashboard">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold text-slate-800 flex items-center gap-2">
            <Target className="w-6 h-6 text-orange-500" />
            Suivi Commercial
          </h1>
          <p className="text-slate-500">Vue d'ensemble du parcours client et alertes</p>
        </div>
        <Button variant="outline" onClick={fetchData} disabled={loading}>
          <RefreshCw className={`w-4 h-4 mr-2 ${loading ? 'animate-spin' : ''}`} />
          Actualiser
        </Button>
      </div>

      {/* Stats Cards */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
        <Card className={alertStats.haute > 0 ? 'border-red-200 bg-red-50/50' : ''}>
          <CardContent className="p-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-slate-500">Alertes urgentes</p>
                <p className="text-2xl font-bold text-red-600">{alertStats.haute || 0}</p>
              </div>
              <div className="p-3 bg-red-100 rounded-lg">
                <AlertTriangle className="w-5 h-5 text-red-600" />
              </div>
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardContent className="p-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-slate-500">Clients en cours</p>
                <p className="text-2xl font-bold text-slate-800">{dashboard?.summary?.clients_en_cours || 0}</p>
              </div>
              <div className="p-3 bg-blue-100 rounded-lg">
                <Users className="w-5 h-5 text-blue-600" />
              </div>
            </div>
            <p className="mt-2 text-sm text-green-600">
              {dashboard?.summary?.clients_100 || 0} complétés à 100%
            </p>
          </CardContent>
        </Card>

        <Card>
          <CardContent className="p-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-slate-500">Devis en cours</p>
                <p className="text-2xl font-bold text-slate-800">{dashboard?.summary?.devis_en_cours || 0}</p>
              </div>
              <div className="p-3 bg-orange-100 rounded-lg">
                <FileText className="w-5 h-5 text-orange-600" />
              </div>
            </div>
            <p className="mt-2 text-sm text-green-600">
              {dashboard?.summary?.devis_100 || 0} complétés à 100%
            </p>
          </CardContent>
        </Card>

        <Card>
          <CardContent className="p-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-slate-500">Total alertes</p>
                <p className="text-2xl font-bold text-slate-800">{alertStats.total || 0}</p>
              </div>
              <div className="p-3 bg-slate-100 rounded-lg">
                <Bell className="w-5 h-5 text-slate-600" />
              </div>
            </div>
            <div className="mt-2 flex gap-2 text-xs">
              <span className="text-orange-600">{alertStats.moyenne || 0} moyennes</span>
              <span className="text-yellow-600">{alertStats.basse || 0} basses</span>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Main Content */}
      <Tabs defaultValue="alerts" className="space-y-4">
        <TabsList>
          <TabsTrigger value="alerts" className="flex items-center gap-2">
            <AlertTriangle className="w-4 h-4" />
            Alertes ({alertStats.total || 0})
          </TabsTrigger>
          <TabsTrigger value="clients" className="flex items-center gap-2">
            <Users className="w-4 h-4" />
            Clients ({dashboard?.clients?.length || 0})
          </TabsTrigger>
          <TabsTrigger value="devis" className="flex items-center gap-2">
            <FileText className="w-4 h-4" />
            Devis ({dashboard?.devis?.length || 0})
          </TabsTrigger>
        </TabsList>

        {/* Alertes Tab */}
        <TabsContent value="alerts">
          <Card>
            <CardHeader className="pb-3">
              <div className="flex items-center justify-between">
                <CardTitle className="text-lg">Alertes de retard</CardTitle>
                <Select value={filterPriorite} onValueChange={setFilterPriorite}>
                  <SelectTrigger className="w-40">
                    <SelectValue placeholder="Filtrer..." />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">Toutes ({alertStats.total || 0})</SelectItem>
                    <SelectItem value="haute">🔴 Haute ({alertStats.haute || 0})</SelectItem>
                    <SelectItem value="moyenne">🟠 Moyenne ({alertStats.moyenne || 0})</SelectItem>
                    <SelectItem value="basse">🟡 Basse ({alertStats.basse || 0})</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </CardHeader>
            <CardContent>
              {filteredAlerts.length === 0 ? (
                <div className="text-center py-12">
                  <CheckCircle2 className="w-12 h-12 text-green-500 mx-auto mb-3" />
                  <p className="text-lg font-medium text-slate-800">Aucune alerte !</p>
                  <p className="text-slate-500">Tous les parcours sont à jour</p>
                </div>
              ) : (
                <div className="space-y-3">
                  {filteredAlerts.map((alert, idx) => {
                    const etapeConfig = ETAPE_CONFIG[alert.etape] || {};
                    const EtapeIcon = etapeConfig.icon || Clock;
                    const prioriteConfig = PRIORITE_CONFIG[alert.priorite] || PRIORITE_CONFIG.moyenne;
                    
                    return (
                      <div 
                        key={idx} 
                        className={`flex items-center justify-between p-4 rounded-lg border ${
                          alert.priorite === 'haute' ? 'bg-red-50 border-red-200' :
                          alert.priorite === 'moyenne' ? 'bg-orange-50 border-orange-200' :
                          'bg-yellow-50 border-yellow-200'
                        }`}
                      >
                        <div className="flex items-center gap-4">
                          <div className={`p-2 rounded-lg ${etapeConfig.color || 'bg-slate-100'}`}>
                            <EtapeIcon className="w-4 h-4" />
                          </div>
                          <div>
                            <div className="flex items-center gap-2">
                              <p className="font-medium text-slate-800">{alert.message}</p>
                              <Badge variant="outline" className={prioriteConfig.color}>
                                {prioriteConfig.icon} {prioriteConfig.label}
                              </Badge>
                            </div>
                            <div className="flex items-center gap-2 mt-1 text-sm text-slate-500">
                              <span className={alert.type === 'client' ? 'text-blue-600' : 'text-orange-600'}>
                                {alert.type === 'client' ? '👤' : '📄'} {alert.entity_name}
                              </span>
                              <span>•</span>
                              <span className="text-red-600 font-medium">{formatRetard(alert.retard_heures)}</span>
                            </div>
                          </div>
                        </div>
                        <Link 
                          to={alert.type === 'client' 
                            ? `/admin/clients/${alert.entity_id}` 
                            : `/admin/devis/${alert.entity_id}`
                          }
                        >
                          <Button variant="ghost" size="sm">
                            <Eye className="w-4 h-4 mr-1" />
                            Voir
                          </Button>
                        </Link>
                      </div>
                    );
                  })}
                </div>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        {/* Clients Tab */}
        <TabsContent value="clients">
          <Card>
            <CardHeader>
              <CardTitle className="text-lg">Progression des clients</CardTitle>
            </CardHeader>
            <CardContent>
              {dashboard?.clients?.length === 0 ? (
                <p className="text-center text-slate-400 py-8">Aucun client avec parcours en cours</p>
              ) : (
                <div className="space-y-3">
                  {dashboard?.clients?.map((client) => (
                    <div key={client.id} className="flex items-center justify-between p-3 bg-slate-50 rounded-lg">
                      <div className="flex items-center gap-3 flex-1">
                        <div>
                          <p className="font-medium text-slate-800">{client.name}</p>
                          {client.company && <p className="text-sm text-slate-500">{client.company}</p>}
                        </div>
                      </div>
                      <div className="flex items-center gap-4">
                        <div className="w-32">
                          <Progress value={client.progress} className="h-2" />
                        </div>
                        <span className={`text-sm font-medium ${
                          client.progress === 100 ? 'text-green-600' :
                          client.progress >= 50 ? 'text-orange-600' : 'text-slate-600'
                        }`}>
                          {client.progress}%
                        </span>
                        <Link to={`/admin/clients/${client.id}`}>
                          <Button variant="ghost" size="sm">
                            <ArrowRight className="w-4 h-4" />
                          </Button>
                        </Link>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        {/* Devis Tab */}
        <TabsContent value="devis">
          <Card>
            <CardHeader>
              <CardTitle className="text-lg">Progression des devis</CardTitle>
            </CardHeader>
            <CardContent>
              {dashboard?.devis?.length === 0 ? (
                <p className="text-center text-slate-400 py-8">Aucun devis avec parcours en cours</p>
              ) : (
                <div className="space-y-3">
                  {dashboard?.devis?.map((devis) => (
                    <div key={devis.id} className="flex items-center justify-between p-3 bg-slate-50 rounded-lg">
                      <div className="flex items-center gap-3 flex-1">
                        <div>
                          <p className="font-medium text-slate-800">{devis.number}</p>
                          <p className="text-sm text-slate-500">{devis.client_name}</p>
                        </div>
                      </div>
                      <div className="flex items-center gap-4">
                        <Badge variant={
                          devis.status === 'validated' ? 'default' :
                          devis.status === 'pending' ? 'secondary' : 'outline'
                        } className={
                          devis.status === 'validated' ? 'bg-green-100 text-green-700' :
                          devis.status === 'pending' ? 'bg-orange-100 text-orange-700' : ''
                        }>
                          {devis.status === 'validated' ? 'Validé' :
                           devis.status === 'pending' ? 'En attente' :
                           devis.status === 'draft' ? 'Brouillon' : devis.status}
                        </Badge>
                        <div className="w-24">
                          <Progress value={devis.progress} className="h-2" />
                        </div>
                        <span className="text-sm font-medium text-slate-600 w-10">{devis.progress}%</span>
                        <Link to={`/admin/devis/${devis.id}`}>
                          <Button variant="ghost" size="sm">
                            <ArrowRight className="w-4 h-4" />
                          </Button>
                        </Link>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>
    </div>
  );
};

export default ParcoursDashboardPage;
